import { useBreakpointValue } from "@mallardbay/lib-react-components"

interface ResponsiveValueOptions {
    readonly fallback?: string
}

/**
 * Wrapper around useBreakpointValue that enforces ssr: true for server-side rendering support.
 * Use this instead of useBreakpointValue directly.
 */
export function useResponsiveValue<T>(
    values: Partial<Record<string, T>> | T[],
    options?: ResponsiveValueOptions
): T | undefined {
    return useBreakpointValue(values, { ...options, ssr: true })
}

/**
 * Returns true if the current viewport is mobile (base breakpoint).
 * Mobile is defined as screens smaller than md breakpoint.
 */
export function useIsMobile() {
    return useResponsiveValue({ base: true, md: false }) ?? false
}

/**
 * Returns true if the current viewport is tablet (md breakpoint only).
 * Tablet is defined as screens at md breakpoint but not lg or larger.
 */
export function useIsTablet() {
    return (
        useResponsiveValue({ base: false, sm: false, md: true, lg: false }) ??
        false
    )
}

/**
 * Returns true if the current viewport is desktop (lg breakpoint or larger).
 * Desktop is defined as screens at lg or xl breakpoints.
 */
export function useIsDesktop() {
    return (
        useResponsiveValue({
            base: false,
            sm: false,
            md: false,
            lg: true,
            xl: true,
        }) ?? false
    )
}

/**
 * Returns true if the current viewport is wide desktop (xl breakpoint only).
 * Wide desktop is defined as screens at xl breakpoint.
 */
export function useIsWideDesktop() {
    return (
        useResponsiveValue({
            base: false,
            sm: false,
            md: false,
            lg: false,
            xl: true,
        }) ?? false
    )
}

/**
 * Returns true if the current viewport is medium screen (md breakpoint only).
 * Medium screen is defined as screens at md breakpoint but not lg or larger.
 */
export function useIsMediumScreen() {
    return useResponsiveValue({
        base: false,
        md: true,
        lg: false,
    })
}

/**
 * Returns true if the current viewport is smaller than medium screen.
 * Smaller than medium is defined as screens at base or sm breakpoints.
 */
export default function useIsSmallerThanMediumScreen() {
    return useResponsiveValue({ base: true, sm: true, md: false })
}

/**
 * Returns true if the current viewport is smaller than small screen.
 * Smaller than small is defined as screens at the base breakpoint only
 * (i.e. phone-sized, below sm).
 */
export function useIsSmallerThanSmallScreen() {
    return useResponsiveValue({ base: true, sm: false }) ?? false
}

/**
 * Returns true if the current viewport is less than large screen.
 * Less than large is defined as screens smaller than lg breakpoint.
 */
export function useIsLessThanLargeScreen() {
    return useResponsiveValue({
        base: true,
        lg: false,
    })
}
